// Agent-free ("cut out the agent") transaction playbooks for Singapore
// residential property. Each flow is a stage-by-stage checklist with the
// official portals, so a buyer/seller can transact directly. Informational —
// not legal advice; a conveyancing lawyer still handles the legal work (that's
// not the agent).
//
// Cost helpers reuse the app's stamp-duty math. Nothing here is scraped.

import { buyerStampDuty } from "./affordability";
import { absdFor, COST_ASSUMPTIONS, type Buyer } from "../finance/costs";

export interface FlowStep {
  id: string;
  title: string;
  detail: string;
  link?: { label: string; url: string };
  cost?: string; // human note on typical cost at this step
}

export type FlowClass = "hdb" | "private" | "car";

export interface Flow {
  id: string;
  label: string;
  side: "buy" | "sell";
  klass: FlowClass;
  blurb: string;
  steps: FlowStep[];
}

const HDB_PORTAL = { label: "HDB Flat Portal", url: "https://homes.hdb.gov.sg/" };
const IRAS_STAMP = { label: "IRAS e-Stamping", url: "https://mytax.iras.gov.sg/" };
const CPF = { label: "CPF housing", url: "https://www.cpf.gov.sg/member/home-ownership" };
const HDB_GRANTS = { label: "HDB CPF Housing Grants", url: "https://www.hdb.gov.sg/residential/buying-a-flat/understanding-your-eligibility-and-flat-and-grant-options/cpf-housing-grants/resale-flat" };
const ONEMOTORING = { label: "OneMotoring (LTA)", url: "https://onemotoring.lta.gov.sg/" };
const VEHICLE_TRANSFER = { label: "Transfer vehicle ownership", url: "https://onemotoring.lta.gov.sg/content/onemotoring/home/buying/ownership-transfer.html" };

export const FLOWS: Flow[] = [
  {
    id: "hdb-buy",
    label: "Buy an HDB resale flat",
    side: "buy",
    klass: "hdb",
    blurb: "Buy directly from a seller with no agent — HDB's portal drives the whole process.",
    steps: [
      { id: "hfe", title: "Get your HDB Flat Eligibility (HFE) letter", detail: "Apply on the HDB Flat Portal with Singpass. It confirms your eligibility, grants, and the maximum HDB loan — do this first; it's valid before you commit.", link: HDB_PORTAL },
      { id: "grants", title: "Check the grants you qualify for", detail: "This is what an agent charges to 'maximise' — but the HFE already computes it. First-timer families may get the CPF Housing Grant (up to $80k, by flat size), the Enhanced CPF Housing Grant (EHG, up to $120k on a sliding scale by household income, income ceiling $9,000), and the Proximity Housing Grant ($30k living with / $20k near parents or a child). Amounts change with policy — confirm your exact figures via the HFE / HDB calculator.", link: HDB_GRANTS },
      { id: "fin", title: "Confirm financing", detail: "HDB loan (from the HFE) or a bank loan — for a bank loan get an Approval-in-Principle so you know your limit. Net your grants off the price, then line up CPF OA and cash for the downpayment and duties.", link: CPF },
      { id: "search", title: "Search & view flats directly", detail: "Find listings and arrange viewings with sellers yourself. Note the flat's remaining lease, storey, and recent transacted prices (use the Under-valued panel here to spot deals)." },
      { id: "negotiate", title: "Negotiate price", detail: "Agree a price with the seller. Any amount above HDB's valuation is Cash-Over-Valuation (COV), payable in cash — so the valuation matters." },
      { id: "otp", title: "Receive the Option to Purchase (OTP)", detail: "The seller grants you the OTP. You pay an option fee (max $1,000). You then have the option period (usually 21 days) to exercise.", cost: "Option fee ≤ $1,000" },
      { id: "value", title: "Request Value from HDB", detail: "After the OTP is granted, submit a Request for Value on the portal so you know the official valuation (and any COV) before you exercise.", link: HDB_PORTAL },
      { id: "exercise", title: "Exercise the OTP", detail: "Within the option period, sign to exercise and pay the exercise fee. Option + exercise fees together are capped at $5,000.", cost: "Option + exercise ≤ $5,000" },
      { id: "apply", title: "Submit the resale application", detail: "Both buyer and seller submit their portions of the resale application on the HDB portal. HDB reviews eligibility and documents.", link: HDB_PORTAL },
      { id: "stamp", title: "Pay Buyer's Stamp Duty", detail: "Pay BSD via IRAS e-Stamping within 14 days of exercising. HDB can act as your conveyancer, or engage a private law firm (~$1.8k).", link: IRAS_STAMP, cost: "BSD + legal ~$1.8k" },
      { id: "complete", title: "Completion & keys", detail: "Attend the completion appointment at HDB. The loan is disbursed, CPF/cash settled, and keys are handed over." },
    ],
  },
  {
    id: "hdb-sell",
    label: "Sell your HDB flat",
    side: "sell",
    klass: "hdb",
    blurb: "List and sell directly — check MOP first, then the portal handles the rest.",
    steps: [
      { id: "mop", title: "Confirm your Minimum Occupation Period (MOP)", detail: "You must have occupied the flat for 5 years before you can sell. Check your MOP date on the HDB portal.", link: HDB_PORTAL },
      { id: "intent", title: "Register Intent to Sell", detail: "Register on the HDB Flat Portal. It confirms your eligibility to sell and any resale levy.", link: HDB_PORTAL },
      { id: "price", title: "Price the flat", detail: "Check recent transacted prices for your block/type. Work out your outstanding loan, the CPF (principal + accrued interest) to refund, and any resale levy — that's your net proceeds." },
      { id: "otp", title: "Grant the OTP", detail: "Once you have a buyer, grant the Option to Purchase and collect the option fee (≤ $1,000)." },
      { id: "apply", title: "Submit the resale application", detail: "After the buyer exercises, both parties submit the resale application on the portal.", link: HDB_PORTAL },
      { id: "complete", title: "Completion", detail: "At completion the outstanding loan is discharged, your CPF is refunded, and sale proceeds are released." },
    ],
  },
  {
    id: "private-buy",
    label: "Buy a private property",
    side: "buy",
    klass: "private",
    blurb: "Deal directly with the seller; a conveyancing lawyer (not an agent) does the legal work.",
    steps: [
      { id: "fin", title: "Line up financing", detail: "Get a bank Approval-in-Principle. Know your LTV (75% first loan / 45% second), TDSR limit, and the cash/CPF you need for the downpayment.", link: CPF },
      { id: "lawyer", title: "Engage a conveyancing lawyer", detail: "This is the professional you actually need — not an agent. A conveyancing firm handles title, contracts and mortgage. Typical fee ~$2.5–3k.", cost: "Legal ~$2.5–3k" },
      { id: "negotiate", title: "Negotiate & receive the OTP", detail: "Agree a price with the seller and receive the Option to Purchase. You typically pay a 1% option fee to secure it." },
      { id: "exercise", title: "Exercise the OTP", detail: "Within the option period (usually 14 days) sign the OTP / S&P to exercise, paying the balance of the deposit (typically another 4%, i.e. 5% total)." },
      { id: "stamp", title: "Pay BSD (+ ABSD)", detail: "Pay Buyer's Stamp Duty — and ABSD if this isn't your only property — via IRAS within 14 days of exercising. Your lawyer usually handles the e-Stamping.", link: IRAS_STAMP },
      { id: "complete", title: "Completion", detail: "Your lawyer completes conveyancing (~8–12 weeks): title search, mortgage, and the balance paid via loan/CPF/cash. Then you collect the keys." },
    ],
  },
  {
    id: "private-sell",
    label: "Sell a private property",
    side: "sell",
    klass: "private",
    blurb: "Sell directly; a conveyancing lawyer handles completion.",
    steps: [
      { id: "position", title: "Work out your position", detail: "Check for Seller's Stamp Duty (held under 4 years if bought on or after 4 Jul 2025; under 3 years if bought earlier), your outstanding loan, and the CPF to refund. The Sell → Buy planner on this tab does this split for you." },
      { id: "lawyer", title: "Engage a conveyancing lawyer", detail: "Appoint a firm to handle the sale contract and discharge of your mortgage. ~$2.5–3k.", cost: "Legal ~$2.5–3k" },
      { id: "otp", title: "Grant the OTP", detail: "Once you have a buyer, grant the Option to Purchase and collect the 1% option fee." },
      { id: "complete", title: "Completion", detail: "After the buyer exercises and signs the S&P, your lawyer completes the sale, discharges the loan, refunds CPF, and releases the proceeds." },
    ],
  },
  {
    id: "car-buy",
    label: "Buy a used car (direct from seller)",
    side: "buy",
    klass: "car",
    blurb: "Buy privately and skip the dealer margin — LTA's portal does the transfer.",
    steps: [
      { id: "fin", title: "Sort financing", detail: "Car loans are capped at 60% of price/value (for OMV > $20k) over max 7 years. Get an in-principle approval so you know your ceiling, and line up the ~40% cash downpayment." },
      { id: "inspect", title: "Inspect the car & its record", detail: "View the car, check service history and mileage, and get an independent workshop inspection (~$100–150). Look up the vehicle's details (COE expiry, OMV, road tax) on OneMotoring.", link: ONEMOTORING },
      { id: "value", title: "Verify value & the rebate floor", detail: "Check the COE time left and the guaranteed PARF/COE de-registration rebate — that's the floor value. Confirm there's no outstanding finance on the car (it must be cleared before transfer).", link: ONEMOTORING },
      { id: "negotiate", title: "Negotiate the price", detail: "Agree a price with the seller. Compare against similar cars' depreciation-per-year, not just sticker price." },
      { id: "insure", title: "Arrange insurance", detail: "You need valid motor insurance in your name before you can take over the vehicle. Get a quote and bind cover effective from the transfer date." },
      { id: "transfer", title: "Transfer ownership on OneMotoring", detail: "Both parties log in with Singpass to complete the transfer (fee $11). Ensure road tax is valid and insurance is in place. Pay the seller on completion.", link: VEHICLE_TRANSFER, cost: "Transfer fee $11" },
      { id: "handover", title: "Handover", detail: "Collect the keys, both remotes, the owner's manual, service records and any warranty. Confirm the transfer shows in your OneMotoring account." },
    ],
  },
  {
    id: "car-sell",
    label: "Sell your car (direct to buyer)",
    side: "sell",
    klass: "car",
    blurb: "Sell privately to keep the dealer margin — clear any loan, then transfer.",
    steps: [
      { id: "loan", title: "Clear any outstanding loan", detail: "A car with an existing loan can't be transferred until the finance is settled. Get the redemption amount from your finance company." },
      { id: "value", title: "Know your options & value", detail: "Compare selling to a buyer vs de-registering (PARF/COE rebate) or exporting. The rebate is your guaranteed floor; a private sale usually beats it.", link: ONEMOTORING },
      { id: "advertise", title: "Advertise & agree a price", detail: "List the car directly and negotiate. Be ready to show service history and allow an inspection." },
      { id: "transfer", title: "Transfer ownership on OneMotoring", detail: "Complete the transfer with the buyer via Singpass (the buyer needs insurance + valid road tax). Receive payment on completion.", link: VEHICLE_TRANSFER, cost: "Transfer fee $11" },
      { id: "insure", title: "Cancel your insurance", detail: "After the transfer, cancel your motor insurance for a pro-rated refund of the unused premium." },
    ],
  },
];

// --- Move composition: the agent-free playbook for a whole move -------------
// A move often chains two transactions (sell one home, buy the next) that may
// be different property types — e.g. sell an HDB flat and buy private. This
// composes the right pair of flows, in the right order for the chosen sequence.

export interface MovePhase { phase: "sell" | "buy"; flow: Flow }

export function moveFlows(opts: {
  scenario: string; // MoveScenario id
  currentClass: FlowClass | null; // the home being sold
  nextClass: FlowClass; // the home being bought
  sells: boolean;
}): MovePhase[] {
  const sellFlow = opts.currentClass
    ? FLOWS.find((f) => f.klass === opts.currentClass && f.side === "sell")
    : undefined;
  const buyFlow = FLOWS.find((f) => f.klass === opts.nextClass && f.side === "buy");
  const seq: MovePhase[] = [];
  const pushSell = () => { if (opts.sells && sellFlow) seq.push({ phase: "sell", flow: sellFlow }); };
  const pushBuy = () => { if (buyFlow) seq.push({ phase: "buy", flow: buyFlow }); };
  // Buy-first secures the next home before selling; every other sequence that
  // sells (sell-first, downsize) does the sale first. Keep-buy-second has no
  // sale, so only the buy flow is emitted.
  if (opts.scenario === "buy-first") { pushBuy(); pushSell(); }
  else { pushSell(); pushBuy(); }
  return seq;
}

// Cross-transaction sequencing guidance for a move — the order-and-timing gates
// that sit *between* the two playbooks (ABSD/LTV tier, bridging, MOP, the
// Private→HDB wait-out). Type-aware, so a cross-type move gets the right notes.
export function moveSequencing(opts: {
  scenario: string;
  currentClass: FlowClass | null;
  nextClass: FlowClass;
  sells: boolean;
}): string[] {
  const notes: string[] = [];
  if (opts.scenario === "buy-first") {
    notes.push("Buy-first order: you'll pay ABSD upfront on the new home and likely need a bridging loan until the sale completes. Married couples can claim ABSD remission if the first home is sold within 6 months of buying a completed property.");
  } else if (opts.sells) {
    notes.push("Sell-first order: firm up the sale (at least a granted OTP) before committing to the purchase, so you buy as a replacement buyer — no second-property ABSD, the higher 75% LTV, and your sale proceeds + refunded CPF are in hand for the downpayment.");
  }
  if (opts.currentClass === "hdb" && opts.sells) {
    notes.push("HDB sale gate: you can only sell after meeting the 5-year Minimum Occupation Period — register Intent to Sell before granting an OTP.");
  }
  if (opts.currentClass === "private" && opts.nextClass === "hdb" && opts.sells) {
    notes.push("Private → HDB wait-out: after selling the private property you generally wait 15 months before buying a non-subsidised HDB resale flat — plan interim housing for the gap.");
  }
  if (!opts.sells && opts.nextClass === "private") {
    notes.push("Keeping your current home makes this a second-property purchase: ABSD applies and the loan is capped at the lower second-property LTV (45%).");
  }
  if (opts.sells && opts.currentClass && opts.currentClass !== opts.nextClass) {
    notes.push(`Cross-type move: you're running two different playbooks — a ${opts.currentClass.toUpperCase()} sale and a ${opts.nextClass.toUpperCase()} purchase — each with its own portal and paperwork. Work the numbered steps of both below.`);
  }
  return notes;
}

export interface CostLine { label: string; amount: number | null; note?: string }

// Estimated agent-free cost summary for a buy flow at a given price.
export function buyCostSummary(klass: FlowClass, price: number, ownsOther: boolean, buyer?: Buyer): CostLine[] {
  if (klass === "car") {
    // No stamp duty on cars; the DIY saving is the dealer margin on a used car.
    const dealerSaved = Math.round(price * 0.05);
    return [
      { label: "LTA ownership transfer fee", amount: 11 },
      { label: "Independent inspection", amount: 130, note: "recommended" },
      { label: "Insurance (annual)", amount: null, note: "varies — get a quote" },
      { label: "Dealer margin avoided (~5%)", amount: -dealerSaved, note: "buying privately" },
    ];
  }
  const bsd = buyerStampDuty(price);
  // Same ABSD / legal / agent figures as the cost engine (finance/costs.ts).
  const ab = absdFor(price, buyer ?? { residency: "SC", married: false, age: 35 }, ownsOther ? 2 : 1, false);
  const absd = ab.amount;
  const legal = klass === "hdb" ? COST_ASSUMPTIONS.legalFee.hdb : COST_ASSUMPTIONS.legalFee.private;
  const options = klass === "hdb" ? 5_000 : Math.round(price * 0.05); // exercise deposits (recoverable into price for private)
  // Typical buyer agent commission avoided, the DIY saving.
  const agentSaved = Math.round(price * COST_ASSUMPTIONS.buyerAgentRate);
  const lines: CostLine[] = [
    { label: "Buyer's Stamp Duty", amount: bsd },
    { label: "ABSD", amount: absd, note: absd > 0 ? `${Math.round(ab.rate * 100)}%${ab.refundable ? " (refundable)" : ""}` : "none" },
    { label: klass === "hdb" ? "Option + exercise (capped)" : "Option/deposit (~5%, applied to price)", amount: options },
    { label: "Legal / conveyancing", amount: legal },
    { label: `Agent fee avoided (~${Math.round(COST_ASSUMPTIONS.buyerAgentRate * 100)}%)`, amount: -agentSaved, note: "by doing it yourself" },
  ];
  if (klass === "hdb") {
    lines.push({ label: "HDB grants (if first-timer)", amount: null, note: "offsets price — confirm via HFE / HDB calculator" });
  }
  return lines;
}

// A copyable offer / OTP-terms template — a starting point for the parties;
// always have the lawyer review the actual OTP.
export function offerTemplate(price: number, klass: FlowClass): string {
  if (klass === "car") {
    return [
      "VEHICLE SALE AGREEMENT (template — for discussion)",
      "",
      "Vehicle reg. no: [SGX____]",
      "Make / model / year: [____]",
      "COE expiry: [____]        Mileage: [____] km",
      `Agreed price: S$${price.toLocaleString()}`,
      "Deposit on agreement: $[____]   Balance on transfer: $[____]",
      "Transfer via LTA OneMotoring on: [date]",
      "Seller confirms: no outstanding finance; valid road tax; accurate mileage.",
      "Buyer confirms: motor insurance arranged effective from transfer date.",
      "",
      "Seller: [name / NRIC]        Buyer: [name / NRIC]",
      "Date: [date]",
      "",
      "Note: Ownership transfers only when completed on OneMotoring by both parties.",
    ].join("\n");
  }
  const optionFee = klass === "hdb" ? 1_000 : Math.round(price * 0.01);
  return [
    "OFFER TO PURCHASE (template — for discussion; not a binding contract)",
    "",
    `Property: [address / block / unit]`,
    `Property type: ${klass === "hdb" ? "HDB resale flat" : "Private residential"}`,
    `Offer price: S$${price.toLocaleString()}`,
    `Option fee on grant of OTP: S$${optionFee.toLocaleString()}`,
    klass === "hdb" ? "Option period: 21 days" : "Option period: 14 days",
    "Completion: subject to the Sale & Purchase Agreement / HDB timeline",
    "Conditions: subject to satisfactory valuation and financing",
    "",
    "Buyer: [name / NRIC]        Seller: [name / NRIC]",
    "Date: [date]",
    "",
    "Note: This is a plain-language template. The actual Option to Purchase must",
    "be prepared/reviewed by your conveyancing lawyer (or HDB for resale flats).",
  ].join("\n");
}
